import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Card from '../common/Card/Card';
import { useAuth } from '../../hooks/useAuth';
import { AdminUserDetail as AdminUserDetailType, AdminUserFeedback } from '../../types/adminUser.types';
import { TOKEN_STORAGE_KEY } from '../../utils/auth';

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  PROCESSING: 'bg-yellow-100 text-yellow-700',
  PENDING: 'bg-gray-100 text-gray-600',
  IN_QUEUE: 'bg-gray-100 text-gray-600',
  DATA_COLLECTED: 'bg-blue-100 text-blue-700',
  VALIDATION: 'bg-blue-100 text-blue-700',
  NORMALIZATION: 'bg-blue-100 text-blue-700',
  EMAIL_DISCOVERY: 'bg-blue-100 text-blue-700',
};

const FEEDBACK_TYPE_LABELS: Record<string, string> = {
  onboarding: 'ONBOARDING',
  post_search_survey: 'POST-SEARCH SURVEY',
  feedback: 'FEEDBACK',
  bug: 'BUG',
};

const FEEDBACK_TYPE_COLORS: Record<string, string> = {
  onboarding: 'bg-purple-100 text-purple-700',
  post_search_survey: 'bg-blue-100 text-blue-700',
  feedback: 'bg-green-100 text-green-700',
  bug: 'bg-red-100 text-red-700',
};

const parseSurveyContent = (content: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(content);
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
};

const SurveyContent: React.FC<{ content: string }> = ({ content }) => {
  const parsed = parseSurveyContent(content);
  if (!parsed) {
    return <p className="text-sm text-gray-700 whitespace-pre-wrap">{content}</p>;
  }

  const fields: { label: string; key: string }[] = [
    { label: 'Did it help?', key: 'did_help_goal' },
    { label: 'Follow-up', key: 'follow_up_feedback' },
    { label: 'Disliked', key: 'disliked' },
    { label: 'Would pay for', key: 'would_pay_for' },
    { label: 'Additional feedback', key: 'additional_feedback' },
  ];

  return (
    <dl className="space-y-1">
      {fields.map(({ label, key }) => {
        const value = parsed[key];
        if (!value || (Array.isArray(value) && value.length === 0)) return null;
        const display = Array.isArray(value) ? value.join(', ') : String(value);
        return (
          <div key={key} className="flex gap-2 text-sm">
            <dt className="font-medium text-gray-500 shrink-0">{label}:</dt>
            <dd className="text-gray-700">{display}</dd>
          </div>
        );
      })}
    </dl>
  );
};

const ONBOARDING_FIELDS_OLD: { label: string; key: string }[] = [
  { label: 'Goal', key: 'goal' },
  { label: 'Priority', key: 'priority' },
  { label: 'Volume', key: 'volume' },
  { label: 'Workflow', key: 'workflow' },
];

const ONBOARDING_FIELDS_NEW: { label: string; key: string }[] = [
  { label: 'Persona', key: 'persona' },
  { label: 'Target business types', key: 'target_business_types' },
  { label: 'Target country', key: 'target_country' },
  { label: 'Target region', key: 'target_region' },
  { label: 'Outreach channel', key: 'outreach_channel' },
  { label: 'Success definition', key: 'success_definition' },
];

const OnboardingContent: React.FC<{ content: string }> = ({ content }) => {
  const parsed = parseSurveyContent(content);
  if (!parsed) return <p className="text-sm text-gray-700 whitespace-pre-wrap">{content}</p>;
  const fields = 'goal' in parsed ? ONBOARDING_FIELDS_OLD : ONBOARDING_FIELDS_NEW;
  return (
    <dl className="space-y-1">
      {fields.map(({ label, key }) => {
        const value = parsed[key];
        if (!value || (Array.isArray(value) && value.length === 0)) return null;
        const display = Array.isArray(value) ? value.join(', ') : String(value);
        return (
          <div key={key} className="flex gap-2 text-sm">
            <dt className="font-medium text-gray-500 shrink-0">{label}:</dt>
            <dd className="text-gray-700">{display}</dd>
          </div>
        );
      })}
    </dl>
  );
};

const FeedbackCard: React.FC<{ fb: AdminUserFeedback }> = ({ fb }) => (
  <div className="border border-gray-200 rounded-md p-4 space-y-2">
    <div className="flex items-center justify-between">
      <span
        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
          FEEDBACK_TYPE_COLORS[fb.type] ?? 'bg-gray-100 text-gray-700'
        }`}
      >
        {FEEDBACK_TYPE_LABELS[fb.type] ?? fb.type.toUpperCase()}
      </span>
      <span className="text-xs text-gray-400">
        {new Date(fb.created_at).toLocaleDateString()}
      </span>
    </div>
    {fb.type === 'onboarding' ? (
      <OnboardingContent content={fb.content} />
    ) : (
      <SurveyContent content={fb.content} />
    )}
  </div>
);

const StatCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="bg-gray-50 rounded-md p-4 text-center">
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{label}</p>
  </div>
);

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const userId = parseInt(id || '0', 10);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [userDetail, setUserDetail] = useState<AdminUserDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!isAuthenticated || !user?.is_admin) return;
      try {
        const token = localStorage.getItem(TOKEN_STORAGE_KEY);
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiUrl}/api/admin/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          setUserDetail(await response.json());
        } else if (response.status === 404) {
          setError('User not found');
        } else {
          setError(`Failed to load user: ${response.status}`);
        }
      } catch {
        setError('Error fetching user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, isAuthenticated, user]);

  if (loading) {
    return (
      <Card>
        <p className="text-sm text-gray-600 py-8 text-center">Loading...</p>
      </Card>
    );
  }

  if (error || !userDetail) {
    return (
      <Card>
        <p className="text-sm text-red-600 py-8 text-center">{error ?? 'User not found'}</p>
        <div className="text-center mt-4">
          <button
            onClick={() => navigate('/admin')}
            className="text-sm text-indigo-600 hover:underline"
          >
            Back to Admin
          </button>
        </div>
      </Card>
    );
  }

  const initials = userDetail.email.slice(0, 2).toUpperCase();
  const planLabel = userDetail.plan_name
    ? `${userDetail.plan_name}${
        userDetail.plan_price_cents
          ? ` · $${(userDetail.plan_price_cents / 100).toFixed(0)}/mo`
          : ''
      }`
    : 'Free';

  const lastActiveLabel = userDetail.last_active
    ? new Date(userDetail.last_active).toLocaleDateString()
    : 'Never';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => navigate('/admin')}
          className="text-sm text-indigo-600 hover:underline"
        >
          ← Admin
        </button>
      </div>

      <Card>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-gray-900 truncate">{userDetail.email}</p>
            <p className="text-sm text-gray-500">@{userDetail.username}</p>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
              <span>Signed up {new Date(userDetail.created_at).toLocaleDateString()}</span>
              <span
                className={`px-2 py-0.5 rounded font-medium ${
                  userDetail.plan_name ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {planLabel}
              </span>
              {!userDetail.is_active && (
                <span className="px-2 py-0.5 rounded bg-red-100 text-red-600 font-medium">Inactive</span>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Searches" value={userDetail.search_count} />
        <StatCard label="Enrichments" value={userDetail.enrichments_count} />
        <StatCard label="Verifications" value={userDetail.verifications_count} />
        <StatCard label="Last Active" value={lastActiveLabel} />
      </div>

      <Card>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Searches</h3>
        {userDetail.recent_searches.length === 0 ? (
          <p className="text-sm text-gray-500">No searches yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Query
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Location
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Date
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    People
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Emails
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Enrichments
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Verifications
                  </th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {userDetail.recent_searches.map((s) => (
                  <tr key={s.id}>
                    <td className="px-3 py-2 text-xs text-gray-900 max-w-xs truncate">
                      {s.search_query}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 hidden sm:table-cell">
                      {[s.city_name, s.country].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      <span
                        className={`inline-block px-2 py-0.5 rounded font-medium ${
                          STATUS_COLORS[s.status ?? ''] ?? 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {s.status ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 hidden md:table-cell">
                      {new Date(s.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700 text-center hidden lg:table-cell">
                      {s.people_found}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700 text-center hidden lg:table-cell">
                      {s.emails_found}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700 text-center hidden lg:table-cell">
                      {s.enrichments_done}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700 text-center hidden lg:table-cell">
                      {s.verifications_done}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      <Link
                        to={`/admin/searches/${s.id}`}
                        className="text-indigo-600 hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Feedback & Surveys</h3>
        {userDetail.feedback.length === 0 ? (
          <p className="text-sm text-gray-500">No feedback submitted.</p>
        ) : (
          <div className="space-y-3">
            {userDetail.feedback.map((fb) => (
              <FeedbackCard key={fb.id} fb={fb} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminUserDetail;
